import React, {useState, useCallback, useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {useDispatch, useSelector} from "react-redux";
import {Formik} from "formik";
import {Translation as T, useTranslation, historyTabs} from "@awe-web/shared/lib";
import {Row, Col} from "../../components/grid.js";
import {FormikTextInput,Button, FormikLabeledField} from "../../components/form.js";
import { useInitialize } from "../../util/react.js";
import {Overlay, OverlayHeading, OverlaySection} from "../../components/overlay.js";
import {Pagination} from "../../components/pagination.js";
import {InlineLoadingIndicator} from "../../components/loading_indicator.jsx";
import {
  getReservationHistory,
  getActiveTab,
  getCurrentPage,
  getNumberOfPages,
  getCsvExportUrl,
  getFilterValues
} from "../store.js";
import {
  initializeHistory,
  selectTab,
  nextPage,
  previousPage,
  setFilter,
  cancelReservation
} from "../actions.js";

import "./history.scss";

const TAB_ACTIVE_RESERVATIONS = "FSD0000314";
const TAB_PAST_RESERVATIONS = "FSD0000315";

const OPEN_CANCEL_RESERVATION_OVERLAY = "ALG0000176";
const CANCEL_RESERVATION_TITLE = "FSD0000326";
const CANCEL_RESERVATION_CONFIRMATION = "FSD0000327";
const CANCEL_RESERVATION_BUTTON = "FSD0000329";
const CANCEL_OVERLAY_BUTTON = "FSD0000330";

const CLASSIFICATION = "FSD0000316";
const LICENSE_PLATE = "ALG0000170";
const GROSS_LIST_PRICE = "ALG0000171";
const RESERVATION_NO_SHORT = "FSD0000318";
const DRIVER = "ALG0000172";
const CHECK_OUT_TIMESTAMP = "FSD0000319";
const CHECK_IN_TIMESTAMP = "ALG0000243";
const GROSS_PRICE = "ALG0000174";
const STATE = "FSD0000324";
const ACTION = "FSD0000325";
const NO_RESERVATIONS_FOUND = "FSD0000341";

const HISTORY_LOADING = "FSD0000358";
const DOWNLOAD = "ALG0000166";
const FILTER_LICENSE_PLATE_LABEL = "ALG0000186";
const FILTER_LICENSE_PLATE_PLACEHOLDER = "ALG0000187";
const FILTER_DRIVER_NAME_LABEL = "ALG0000188";
const FILTER_DRIVER_NAME_PLACEHOLDER = "ALG0000189";
const FILTER_BUTTON_LABEL = "ALG0000169";
const NOTICE = "ALG0000175";

const reservationShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  imageUrl1: PropTypes.string,
  classificationDescr: PropTypes.string,
  registrationNumber: PropTypes.string,
  formattedListPriceGross: PropTypes.string,
  reservationNo: PropTypes.number,
  firstDriverName: PropTypes.string,
  formattedCoDateTime: PropTypes.string,
  formattedInvoiceAmountNet: PropTypes.string,
  formattedInvoiceAmountGross: PropTypes.string,
  stateDescription: PropTypes.string,
  isCancelable: PropTypes.bool
});

const cellValue = (value) => value || "—";

function HistoryTabs({selectedTab = historyTabs.ACTIVE, onSelectActive, onSelectPast}) {
  const classNameActiveButton = classnames("history-tabs__button", {
    "history-tabs__button--active": selectedTab === historyTabs.ACTIVE
  });
  const classNamePastButton = classnames("history-tabs__button", {
    "history-tabs__button--active": selectedTab === historyTabs.PAST
  });

  return (
    <div className="history-tabs">
      <button
        className={classNameActiveButton}
        type="button"
        onClick={onSelectActive}
      >
        <T code={TAB_ACTIVE_RESERVATIONS} />
      </button>
      <button
        className={classNamePastButton}
        type="button"
        onClick={onSelectPast}
      >
        <T code={TAB_PAST_RESERVATIONS} />
      </button>
    </div>
  );
};
HistoryTabs.propTypes = {
  selectedTab: PropTypes.oneOf([
    historyTabs.ACTIVE,
    historyTabs.PAST
  ]),
  onSelectActive: PropTypes.func,
  onSelectPast: PropTypes.func
};

function CancelReservationOverlayContent({reservation, confirm, closeOverlay}) {

  const row = (labelCode, value) => (
    <div className="cancel-reservation-overlay-content__row">
      <div>
        <T code={labelCode} />
      </div>
      <div className="cancel-reservation-overlay-content__row__value">
        {cellValue(value)}
      </div>
    </div>
  );

  return (
    <>
      <OverlayHeading><T code={CANCEL_RESERVATION_TITLE} /></OverlayHeading>
      <OverlaySection>
        <p><T code={CANCEL_RESERVATION_CONFIRMATION} /></p>

        <div className="cancel-reservation-overlay-content__rent-object">
          {row(CLASSIFICATION, reservation.classificationDescr)}
          {row(LICENSE_PLATE, reservation.registrationNumber)}
          {row(GROSS_LIST_PRICE, reservation.formattedListPriceGross)}
          {row(RESERVATION_NO_SHORT, reservation.reservationNo)}
          {row(DRIVER, reservation.firstDriverName)}
          {row(CHECK_OUT_TIMESTAMP, reservation.formattedCoDateTime)}
          {row(CHECK_IN_TIMESTAMP, reservation.formattedCiDateTime)}
          {row(GROSS_PRICE, reservation.formattedInvoiceAmountGross)}
          {row(STATE, reservation.stateDescription)}
        </div>

        <div className="action-buttons">
          <Button primary continue onClick={confirm} className="action-buttons__button action-buttons__button--primary">
            <T code={CANCEL_RESERVATION_BUTTON} />
          </Button>
          <Button secondary back onClick={closeOverlay} className="action-buttons__button">
            <T code={CANCEL_OVERLAY_BUTTON} />
          </Button>
        </div>
      </OverlaySection>
    </>
  );
}

CancelReservationOverlayContent.propTypes = {
  confirm: PropTypes.func,
  closeOverlay: PropTypes.func,
  reservation: reservationShape.isRequired
};

function CancelReservationButton({reservation}) {

  const dispatch = useDispatch();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  
  const closeOverlay = useCallback(() => setIsOverlayOpen(false), []);
  
  const onCancelReservation = useCallback(() => {
    closeOverlay();
    dispatch(cancelReservation(reservation.id));
  }, [closeOverlay, dispatch, reservation.id]);

  return (
    <>
      <Button
        tertiary
        className="cancel-reservation-button"
        onClick={() => { setIsOverlayOpen(true); }}
      >
        <T code={OPEN_CANCEL_RESERVATION_OVERLAY} />
      </Button>

      <Overlay
        className="cancel-reservation-button__overlay"
        open={isOverlayOpen}
        onClose={closeOverlay}
      >
        <CancelReservationOverlayContent
          reservation={reservation}
          confirm={onCancelReservation}
          closeOverlay={closeOverlay}
        />
      </Overlay>
    </>
  );
};
CancelReservationButton.propTypes = {
  reservation: CancelReservationOverlayContent.propTypes.reservation,
};

function HistoryTable({showActionColumn}) {
  const {translate} = useTranslation();

  const stateReservations = useSelector((state) => getReservationHistory(state));
  const reservations = useMemo(() => stateReservations ? stateReservations.toJS() : [], [stateReservations]);

  const th = useCallback((labelCode) => (
    <th className="history-table__column-header">
      <T code={labelCode} />
    </th>
  ), []);
  
  const td = useCallback((labelCode, value, className) => (
    <td
      className={classnames("history-table__cell", className)}
      data-label={translate(labelCode)}
    >
      {cellValue(value)}
    </td>
  ), [translate]);

  return (
    <table className="history-table">
      <thead className="history-table__head">
        <tr className="history-table__head__row">
          {th(CLASSIFICATION)}
          {th(LICENSE_PLATE)}
          {th(GROSS_LIST_PRICE)}
          {th(RESERVATION_NO_SHORT)}
          {th(DRIVER)}
          {th(CHECK_OUT_TIMESTAMP)}
          {th(CHECK_IN_TIMESTAMP)}
          {th(GROSS_PRICE)}
          {th(STATE)}
          {showActionColumn
            ? th(ACTION)
            : null
          }
        </tr>
      </thead>
      <tbody className="history-table__body">
        {reservations.length > 0
          ? reservations.map((reservation) => (
              <tr key={reservation.id} className="history-table__body__row">
                {td(CLASSIFICATION, reservation.classificationDescr, "history-table__cell--rent-object-name")}
                {td(LICENSE_PLATE, reservation.registrationNumber)}
                {td(GROSS_LIST_PRICE, reservation.formattedListPriceGross)}
                {td(RESERVATION_NO_SHORT, reservation.reservationNo)}
                {td(DRIVER, reservation.firstDriverName)}
                {td(CHECK_OUT_TIMESTAMP, reservation.formattedCoDateTime)}
                {td(CHECK_IN_TIMESTAMP, reservation.formattedCiDateTime)}
                {td(GROSS_PRICE, reservation.formattedInvoiceAmountGross)}
                {td(STATE, reservation.stateDescription)}
                {showActionColumn
                  ? (
                    <td className="history-table__cell history-table__cell--action">
                      {reservation.isCancelable
                        ? <CancelReservationButton reservation={reservation}/>
                        : null
                      }
                    </td>
                  )
                  : null
                }
              </tr>
          ))
          : (
            <tr>
              <td colSpan={showActionColumn ? 10 : 9} className="history-table__cell history-table__cell--empty">
                <T code={NO_RESERVATIONS_FOUND} />
              </td>
            </tr>
          )
        }
      </tbody>
    </table>
  );
}

HistoryTable.propTypes = {
  showActionColumn: PropTypes.bool
};

function FilterForm({handleSubmit}) {

  const {translate} = useTranslation();

  return (
    <form onSubmit={handleSubmit}>
      <Row alignBottom className="history__filter-form">
        <Col
          className="history__filter-input-col"
          tablet={{cols: 5}}
          desktop={{cols: 3}}
        >
          <FormikLabeledField
            name="registrationNumber"
            getFieldLabel={() => translate(FILTER_LICENSE_PLATE_LABEL)}
            as={FormikTextInput}
            placeholder={translate(FILTER_LICENSE_PLATE_PLACEHOLDER)}
          />
        </Col>
        <Col
          tablet={{cols: 3}}
          desktop={{cols: 0}}
        />
        <Col
          className="history__filter-input-col"
          tablet={{cols: 5}}
          desktop={{cols: 3}}
        >
          <FormikLabeledField
            name="driverName"
            getFieldLabel={() => translate(FILTER_DRIVER_NAME_LABEL)}
            as={FormikTextInput}
            placeholder={translate(FILTER_DRIVER_NAME_PLACEHOLDER)}
          />
        </Col>
        <Col
          tablet={{cols: 3}}
          desktop={{cols: 3}}
        >
          <Button primary filter submit>
            <T code={FILTER_BUTTON_LABEL} />
          </Button>
        </Col>
      </Row>
    </form>
  );
}

FilterForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired
};

export default function History() {
  const dispatch = useDispatch();
  
  const csvExportUrl = useSelector((state) => getCsvExportUrl(state));

  const currentPage = useSelector((state) => getCurrentPage(state));
  const numberOfPages = useSelector((state) => getNumberOfPages(state));
  const onPrevClickHandler = useCallback(() => dispatch(previousPage()), [dispatch]);
  const onNextClickHandler = useCallback(() => dispatch(nextPage()), [dispatch]);
  
  const selectedTab = useSelector((state) => getActiveTab(state));
  const onTabSelected = useCallback((tab) => dispatch(selectTab(tab)), [dispatch]);
  
  const stateFilterValues = useSelector((state) => getFilterValues(state));
  const filterValues = useMemo(() => stateFilterValues.toJS(), [stateFilterValues]);
  const onSubmitFilter = useCallback((formValues) => dispatch(setFilter(formValues)), [dispatch]);
  
  const init = useCallback(async () => await dispatch(initializeHistory()), [dispatch]);
  useInitialize(init);

  useEffect(() => {
    window.scrollTo({top: 0, behavior: "smooth"});
  }, [currentPage]);

  return (
    <>
      <Row>
        <Col tablet={{cols: 4}} desktop={{cols: 6}}>
          <HistoryTabs
            selectedTab={selectedTab}
            onSelectActive={() => onTabSelected(historyTabs.ACTIVE)}
            onSelectPast={() => onTabSelected(historyTabs.PAST)}
          />
        </Col>
        <Col
          className="history__top-download-button-col"
          tablet={{cols: 3, offset: 1}}
          desktop={{cols: 3, offset: 3}}
        >
          <Button secondary download target="_blank" href={csvExportUrl}>
            <T code={DOWNLOAD} />
          </Button>
        </Col>
      </Row>

      <Formik
        enableReinitialize
        initialValues={filterValues}
        onSubmit={onSubmitFilter}
      >
        {(formikProps) => (
          <FilterForm {...formikProps} />
        )}
      </Formik>

      <InlineLoadingIndicator
        typePrefix="HISTORY/RESERVATION_HISTORY"
        message={<T code={HISTORY_LOADING} />}
      >
        <Row className="history__table-row">
          <Col
            tablet={{cols: 4, offset: 2}}
            desktop={{cols: 12, offset: 0}}
          >
            <HistoryTable showActionColumn={selectedTab === historyTabs.ACTIVE}/>
          </Col>
        </Row>

        {numberOfPages > 0
          ? <Row className="history__pagination-row">
              <Col tablet={{cols: 2, offset: 3}} desktop={{cols: 2, offset: 5}} >
                <Pagination
                  currentPage={currentPage}
                  numberOfPages={numberOfPages}
                  onPrevClick={onPrevClickHandler}
                  onNextClick={onNextClickHandler}
                />
              </Col>
            </Row>
          : null
        }
      </InlineLoadingIndicator>

      <Button className="history__bottom-download-button" secondary download href={csvExportUrl} >
        <T code={DOWNLOAD} />
      </Button>

      <T code={NOTICE} />
    </>
  );
}
